package com.swetdot.swetgram.ui.profile

import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.blur
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.ColorMatrix
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.swetdot.swetgram.ui.design.SwetAvatar
import com.swetdot.swetgram.ui.design.SwetBlackBadge
import com.swetdot.swetgram.ui.design.SwetIcon
import com.swetdot.swetgram.ui.design.SwetIconButton
import com.swetdot.swetgram.ui.design.SwetIcons
import com.swetdot.swetgram.ui.design.SwetDropdownMenu
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.design.SwetPressFeedback
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.TouchTarget
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.Radius
import com.swetdot.swetgram.ui.design.ContentWidth
import com.swetdot.swetgram.ui.design.SwetImagePhase
import com.swetdot.swetgram.ui.design.SwetSkeleton
import com.swetdot.swetgram.ui.design.rememberSwetImage
import com.swetdot.swetgram.ui.design.swetTween
import com.swetdot.swetgram.ui.design.Motion
import com.swetdot.swetgram.ui.design.SwetEasing
import com.swetdot.swetgram.ui.design.swetPressable
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens

internal val PublicProfileMaxWidth = ContentWidth.profile
internal val PublicProfileMargin = Space.screenMargin

@Composable
internal fun PublicProfileTopBar(
    title: String,
    backLabel: String,
    moreLabel: String,
    showMore: Boolean,
    menuExpanded: Boolean,
    onBack: () -> Unit,
    onMore: () -> Unit,
    onDismissMenu: () -> Unit,
    menuContent: @Composable () -> Unit,
) {
    SwetTopAppBar(
        title = title,
        onBack = onBack,
        backContentDescription = backLabel,
    ) {
        if (showMore) {
            Box {
                SwetIconButton(contentDescription = moreLabel, onClick = onMore) { tint ->
                    SwetIcon(icon = SwetIcons.More, tint = tint, contentDescription = null)
                }
                SwetDropdownMenu(
                    expanded = menuExpanded,
                    onDismiss = onDismissMenu,
                    content = { menuContent() },
                )
            }
        }
    }
}

@Composable
internal fun PublicProfileMenuItem(
    icon: ImageVector,
    text: String,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors
    DropdownMenuItem(
        text = {
            Text(text, color = if (enabled) colors.textPrimary else colors.textTertiary)
        },
        leadingIcon = {
            SwetIcon(
                icon = icon,
                tint = if (enabled) colors.textPrimary else colors.textTertiary,
                contentDescription = null,
                size = Sizes.iconMedium,
            )
        },
        enabled = enabled,
        onClick = onClick,
        contentPadding = PaddingValues(horizontal = Space.two, vertical = Space.half),
    )
}

@Composable
internal fun PublicProfileHero(
    displayName: String,
    username: String,
    bio: String?,
    avatarUrl: String?,
    presenceLine: String?,
    isOnline: Boolean,
    hasBlackBadge: Boolean,
) {
    ProfileIdentity(
        displayName = displayName,
        username = username,
        bio = bio,
        isOnline = isOnline,
        surfaceColor = SwetgramThemeTokens.colors.backgroundDeep,
        presenceLine = presenceLine,
        avatarUrl = avatarUrl,
        hasBlackBadge = hasBlackBadge,
    )
}

@Composable
internal fun ProfileActionRow(
    primaryText: String,
    primaryIcon: ImageVector,
    primaryEnabled: Boolean,
    primaryLoading: Boolean,
    moreLabel: String,
    menuExpanded: Boolean,
    showMore: Boolean,
    onPrimary: () -> Unit,
    onMore: () -> Unit,
    onDismissMenu: () -> Unit,
    menuContent: @Composable () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(Space.one),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        ProfilePrimaryAction(
            text = primaryText,
            icon = primaryIcon,
            enabled = primaryEnabled,
            loading = primaryLoading,
            onClick = onPrimary,
            modifier = Modifier.weight(1f),
        )
        if (showMore) {
            Box {
                ProfileRoundAction(icon = SwetIcons.More, label = moreLabel, onClick = onMore)
                SwetDropdownMenu(
                    expanded = menuExpanded,
                    onDismiss = onDismissMenu,
                    content = { menuContent() },
                )
            }
        }
    }
}

@Composable
private fun ProfilePrimaryAction(
    text: String,
    icon: ImageVector,
    enabled: Boolean,
    loading: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val colors = SwetgramThemeTokens.colors
    val shape = RoundedCornerShape(Radius.lg)
    Row(
        modifier = modifier
            .height(TouchTarget.fieldHeight)
            .clip(shape)
            .background(colors.card)
            .border(Sizes.borderHairline, colors.border, shape)
            .swetPressable(
                enabled = enabled && !loading,
                role = Role.Button,
                feedback = SwetPressFeedback.StateLayer,
                onClick = onClick,
            ),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        if (loading) {
            CircularProgressIndicator(
                modifier = Modifier.size(Sizes.iconSmallMedium),
                color = colors.textPrimary,
                strokeWidth = Sizes.borderHairline * 2,
            )
        } else {
            SwetIcon(
                icon = icon,
                tint = if (enabled) colors.textPrimary else colors.textTertiary,
                contentDescription = null,
                size = Sizes.iconSmallMedium,
            )
            Spacer(Modifier.width(Space.one))
            Text(
                text = text,
                color = if (enabled) colors.textPrimary else colors.textTertiary,
                style = androidx.compose.material3.MaterialTheme.typography.labelLarge,
            )
        }
    }
}

@Composable
private fun ProfileRoundAction(icon: ImageVector, label: String, onClick: () -> Unit) {
    val colors = SwetgramThemeTokens.colors
    val shape = RoundedCornerShape(Radius.lg)
    Box(
        modifier = Modifier
            .size(TouchTarget.fieldHeight)
            .clip(shape)
            .background(colors.card)
            .border(Sizes.borderHairline, colors.border, shape)
            .swetPressable(role = Role.Button, onClickLabel = label, onClick = onClick),
        contentAlignment = Alignment.Center,
    ) {
        SwetIcon(icon = icon, tint = colors.textPrimary, contentDescription = null, size = Sizes.iconSmallMedium)
    }
}

@Composable
internal fun ProfileInfoCard(
    icon: ImageVector?,
    title: String,
    value: String,
    modifier: Modifier = Modifier,
    leadingText: String? = null,
    trailingIcon: ImageVector? = null,
    trailingLabel: String? = null,
    onTrailingClick: (() -> Unit)? = null,
    labelFirst: Boolean = false,
) {
    val colors = SwetgramThemeTokens.colors
    val shape = RoundedCornerShape(Radius.lg)
    Row(
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = TouchTarget.recommended + Space.two)
            .clip(shape)
            .background(colors.card)
            .border(Sizes.borderHairline, colors.border, shape)
            .padding(horizontal = Space.two, vertical = Space.one),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(Modifier.width(Space.four), contentAlignment = Alignment.CenterStart) {
            if (leadingText != null) {
                Text(
                    text = leadingText,
                    color = colors.textPrimary,
                    style = androidx.compose.material3.MaterialTheme.typography.titleMedium,
                )
            } else if (icon != null) {
                SwetIcon(
                    icon = icon,
                    tint = colors.textPrimary,
                    contentDescription = null,
                    size = Sizes.iconSmallMedium,
                )
            }
        }
        Column(Modifier.weight(1f)) {
            val first = if (labelFirst) title else value
            val second = if (labelFirst) value else title
            Text(
                text = first,
                color = colors.textPrimary,
                style = androidx.compose.material3.MaterialTheme.typography.titleSmall,
                maxLines = 1,
                overflow = if (labelFirst) TextOverflow.Clip else TextOverflow.Ellipsis,
            )
            Spacer(Modifier.height(Sizes.borderHairline))
            Text(
                text = second,
                color = colors.textSecondary,
                style = androidx.compose.material3.MaterialTheme.typography.bodySmall,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
        }
        if (trailingIcon != null && onTrailingClick != null) {
            SwetIconButton(
                contentDescription = trailingLabel.orEmpty(),
                onClick = onTrailingClick,
            ) { tint ->
                SwetIcon(icon = trailingIcon, tint = tint, contentDescription = null, size = Sizes.iconSmallMedium)
            }
        }
    }
}

@Composable
internal fun ProfileSafetyActions(
    reportText: String?,
    blockText: String,
    blockIcon: ImageVector,
    enabled: Boolean,
    onReport: (() -> Unit)?,
    onBlock: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors
    val shape = RoundedCornerShape(Radius.lg)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(shape)
            .background(colors.card)
            .border(Sizes.borderHairline, colors.border, shape),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        if (reportText != null && onReport != null) {
            SafetyRow(SwetIcons.Flag, reportText, enabled, onReport, Modifier.weight(1f))
            Box(Modifier.width(Sizes.borderHairline).height(Space.four).background(colors.border))
        }
        SafetyRow(blockIcon, blockText, enabled, onBlock, Modifier.weight(1f))
    }
}

@Composable
private fun SafetyRow(
    icon: ImageVector,
    text: String,
    enabled: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val colors = SwetgramThemeTokens.colors
    Row(
        modifier = modifier
            .height(TouchTarget.fieldHeight)
            .swetPressable(enabled = enabled, role = Role.Button, onClick = onClick)
            .padding(horizontal = Space.one + Space.half),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(Modifier.width(Space.four), contentAlignment = Alignment.CenterStart) {
            SwetIcon(
                icon = icon,
                tint = if (enabled) colors.textPrimary else colors.textTertiary,
                contentDescription = null,
                size = Sizes.iconSmallMedium,
            )
        }
        Text(
            text = text,
            color = if (enabled) colors.textPrimary else colors.textTertiary,
            style = androidx.compose.material3.MaterialTheme.typography.bodyMedium,
            modifier = Modifier.weight(1f),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
        SwetIcon(
            icon = SwetIcons.ChevronRight,
            tint = colors.textTertiary,
            contentDescription = null,
            size = Sizes.iconSmall,
        )
    }
}

@Composable
internal fun ModernPublicProfileSkeleton() {
    Column(
        modifier = Modifier.fillMaxWidth().padding(horizontal = PublicProfileMargin),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Spacer(Modifier.height(Space.one + Space.half))
        SwetSkeleton(Modifier.size(Sizes.avatarProfile), CircleShape)
        Spacer(Modifier.height(Space.one + Space.half))
        SwetSkeleton(Modifier.fillMaxWidth(0.45f).height(Space.three), RoundedCornerShape(Radius.lg))
        Spacer(Modifier.height(Space.one))
        SwetSkeleton(Modifier.fillMaxWidth(0.36f).height(Sizes.iconSmall), RoundedCornerShape(Radius.sm))
        Spacer(Modifier.height(Space.two))
        SwetSkeleton(Modifier.fillMaxWidth().height(TouchTarget.fieldHeight), RoundedCornerShape(Radius.lg))
        Spacer(Modifier.height(Space.one + Space.half))
        repeat(2) {
            SwetSkeleton(
                Modifier.fillMaxWidth().height(TouchTarget.recommended + Space.two),
                RoundedCornerShape(Radius.lg),
            )
            Spacer(Modifier.height(Space.one))
        }
    }
}
